import { css, cx } from "@emotion/css";
import { borderRadius, space, themeTokens } from "@octopusdeploy/design-system-tokens";
import * as React from "react";
export interface ThirdPartyLogoProps {
    url: string;
    size?: string;
    isDisabled?: boolean;
    className?: string;
    isSquare?: boolean;
}
export function ThirdPartyLogo({ url, size = "3.1rem", isDisabled, className, isSquare }: ThirdPartyLogoProps) {
    const [shouldShowImage, setShouldShowImage] = React.useState(false);
    const placeholderRef = React.useRef<HTMLDivElement>(null);
    useOnVisibilityChanged(placeholderRef, (isVisible) => {
        if (isVisible) {
            setShouldShowImage(isVisible);
        }
    });
    if (!url) {
        return <div />;
    }
    const logoPlaceholder = <div ref={placeholderRef} style={{ width: size, height: size }}></div>;
    const logoImage = <img src={url} style={{ width: size, height: size }} className={cx({ [disabledImageStyles]: isDisabled })} alt="Logo"/>;
    const content = shouldShowImage ? logoImage : logoPlaceholder;
    const borderRadiusClass = isSquare ? squareBorderRadiusStyles : circularBorderRadiusStyles;
    return <div className={cx(logoStyles, borderRadiusClass, className)}>{content}</div>;
}
function useOnVisibilityChanged<T extends HTMLElement>(elementRef: React.RefObject<T>, callback: (isVisible: boolean) => void) {
    React.useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => {
            callback(entry.isIntersecting);
        });
        const currentElement = elementRef?.current;
        if (currentElement) {
            observer.observe(currentElement);
        }
        return () => {
            observer.disconnect();
        };
    }, [callback, elementRef]);
}
const squareBorderRadiusStyles = css({
    borderRadius: borderRadius.small,
});
const circularBorderRadiusStyles = css({
    borderRadius: borderRadius.circle,
});
const logoStyles = css({
    display: "flex",
    width: "fit-content",
    height: "fit-content",
    backgroundColor: themeTokens.color.portal.logo.background,
    "& img": {
        padding: space[4],
        borderRadius: borderRadius.circle,
        display: "block",
        objectFit: "scale-down",
    },
});
const disabledImageStyles = css({
    filter: "grayscale(1)",
    opacity: 0.4,
});
